<template>
  <div class="coro-widget">
    <div
      v-if="widgetConfig.metadata.state === WidgetState.SETUP_REQUIRED"
      data-testid="cloud-apps-empty-data"
      class="coro-widget__content coro-widget--empty"
    >
      <v-icon class="empty-data-icon" icon="$cloud" />
      <div class="subtitle1 text-center">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.noAppsProtected`) }}
      </div>
      <div class="caption text-indigo-medium mb-4">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.noAppsProtectedDescription`) }}
      </div>
      <v-btn
        v-if="canManageCloudApps"
        color="primary"
        rounded
        :to="{ name: RouteName.CLOUD_APPS_PAGE }"
        data-testid="cloud-security-widget-add-protection-btn"
      >
        {{ $t("general.connect") }}
      </v-btn>
    </div>

    <div v-else-if="widgetDataValue.data" class="coro-widget__content">
      <div class="coro-widget__tile">
        <template v-if="widgetDataValue.data.cloudServiceStatuses.length">
          <div class="mt-3 ml-5 mb-5 mt-5">
            <div
              v-for="cloudApp of widgetDataValue.data.cloudServiceStatuses"
              :key="cloudApp.name"
              class="d-flex mb-4"
            >
              <div>
                <coro-icon class="cloud-app-icon" :icon-name="cloudApp.name" />
              </div>
              <div class="ml-3">
                <div class="subtitle2">{{ $t(`services.${cloudApp.name}`) }}</div>
                <div class="d-flex align-center">
                  <div
                    v-if="cloudApp.status !== ServiceStatus.INCOMPLETE"
                    class="inner-circle mr-2"
                    :class="getConnectionStatusColorClass(cloudApp.status)"
                  ></div>
                  <span
                    :class="getStatusClass(cloudApp)"
                    class="body2"
                    @click="openConnectionStatusDialog(cloudApp)"
                  >
                    {{ $t(`services.status.${cloudApp.status}`) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <v-divider></v-divider>
        </template>
      </div>
      <div class="coro-widget__tile">
        <vulnerabilities-tile
          :widget-data="widgetDataValue.data"
          :display-show-more-button="true"
          @all-active-link-clicked="
            viewAllTickets(true, ticketsWidgetFilters.CLOUD_SECURITY_WIDGET)
          "
          @all-caught-up-link-clicked="
            viewAllTickets(false, ticketsWidgetFilters.CLOUD_SECURITY_WIDGET)
          "
          @vulnerability-clicked="
            viewVulnerability($event, ticketsWidgetFilters.CLOUD_SECURITY_WIDGET)
          "
        >
        </vulnerabilities-tile>
        <generated-tickets-trend-chart
          v-if="widgetConfig.metadata.state === WidgetState.HAS_OPEN_TICKETS"
          class="pt-2 px-6"
          :chart-data="widgetDataValue.data.generatedTicketsHistory"
          :delta="widgetDataValue.data.generatedTicketsDelta"
        ></generated-tickets-trend-chart>
      </div>
    </div>

    <widget-error-state
      v-else
      :widget-type="widgetConfig.type"
      @retry-click="$emit('fetch-data')"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType } from "vue";
import { useWidgetContent } from "@/composables/useWidgetContent";
import kebabCase from "lodash/kebabCase";
import CoroIcon from "@/components/CoroIcon.vue";
import { useRoute, useRouter } from "vue-router";
import { TicketsModuleFilter } from "@/constants/tickets";
import { CoroIcons } from "@/constants/coro-icon";
import { Service, ServiceAction, ServiceStatus } from "@/constants/cloud-apps";
import VulnerabilitiesTile from "@/components/VulnerabilitiesTile.vue";
import { RouteName } from "@/constants/routes";
import { trackGtmEvent } from "@/_helpers/analytics";
import {
  componentDialogsConfigConstructor,
  processCloudAppsQueryParams,
} from "@/_helpers/dialog-helpers";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module.ts";
import { ModalWidth } from "@/constants/modals.ts";
import CloudAppConnectionStatusModal from "@/components/modals/cloud-apps/CloudAppConnectionStatusModal.vue";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { storeToRefs } from "pinia";
import { type WidgetConfiguration, WidgetState } from "@/_helpers/dashboard.ts";
import GeneratedTicketsTrendChart from "@/components/dashboard/GeneratedTicketsTrendChart.vue";
import type { WidgetType } from "@/constants/dashboard.ts";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    widgetConfig: {
      type: Object as PropType<WidgetConfiguration<WidgetType.CLOUD_SECURITY>>,
      required: true,
    },
  },
  components: {
    WidgetErrorState,
    GeneratedTicketsTrendChart,
    CoroIcon,
    VulnerabilitiesTile,
  },
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const cloudAppsStore = useCloudAppsStore();
    const dialogsStore = useDialogsStore();
    const { viewVulnerability, viewAllTickets, isAccessRestricted } = useWidgetContent();
    const { services } = storeToRefs(cloudAppsStore);
    const { getServices } = cloudAppsStore;

    const canManageCloudApps = computed(
      () =>
        !isAccessRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.CLOUD_APPS
        )
    );

    const widgetDataValue = computed(() => props.widgetConfig.data);

    const goToUsers = async () => {
      await router.push({ name: RouteName.PROTECTED_USERS });
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${startCase(TicketsModuleFilter.CLOUD_SECURITY_WIDGET)} - ${GTMParams.VIEW_USERS}`
      );
    };

    onMounted(async () => {
      await getServices();

      processCloudAppsQueryParams(route.query);
    });

    const isMicrosoftOrGoogle = (name: Service) => {
      return [Service.G_SUITE, Service.OFFICE_365].includes(name);
    };

    const getConnectionStatusColorClass = (connectionStatus: ServiceStatus) => {
      return {
        "border-0 bg-green-dark": connectionStatus === ServiceStatus.CONNECTED,
        "border-0 bg-yellow-light": connectionStatus === ServiceStatus.CONNECTED_NOT_SECURE,
        "border-0 bg-red-dark": connectionStatus === ServiceStatus.DISCONNECTED,
      };
    };

    const openConnectionStatusDialog = (item: { name: Service }) => {
      const fullObject = services.value.find(({ name }) => name === item.name);

      if (!fullObject) return;

      if (
        !isMicrosoftOrGoogle(fullObject.name) ||
        fullObject.serviceStatus === ServiceStatus.INCOMPLETE
      ) {
        return;
      }

      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${startCase(TicketsModuleFilter.CLOUD_SECURITY_WIDGET)} - ${GTMParams.CONNECTION_STATUS} - ${startCase(fullObject.serviceStatus)}`
      );

      const config = componentDialogsConfigConstructor({
        width: ModalWidth.LARGE,
        action: ServiceAction.CONNECTION_STATUS,
        item: fullObject,
        component: CloudAppConnectionStatusModal,
        disable: false,
        callback: () => {},
        hideFooter: true,
      });

      dialogsStore.openDialog(config);
    };

    const getStatusClass = (cloudApp: { name: Service; status: ServiceStatus }) => {
      return {
        "coro-link":
          cloudApp.status !== ServiceStatus.INCOMPLETE && isMicrosoftOrGoogle(cloudApp.name),
        "text-indigo-medium": cloudApp.status === ServiceStatus.INCOMPLETE,
      };
    };

    return {
      coronetIcons: CoroIcons,
      serviceStatuses: ServiceStatus,
      ticketsWidgetFilters: TicketsModuleFilter,
      canManageCloudApps,
      widgetDataValue,
      getStatusClass,
      isAccessRestricted,
      openConnectionStatusDialog,
      isMicrosoftOrGoogle,
      kebabCase,
      viewAllTickets,
      ServiceStatus,
      viewVulnerability,
      goToUsers,
      RouteName,
      getConnectionStatusColorClass,
      WidgetState,
    };
  },
});
</script>

<style lang="scss" scoped>
.cloud-app-icon {
  height: 48px;
  width: 48px;
}
</style>
